"use client";

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Field,
  FieldGroup,
  FieldLabel,
  FieldDescription,
} from "@/components/ui/field";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, AlertCircle, CheckCircle, Loader2, Info } from "lucide-react";
import { searchVehicle } from "@/app/lib/actions/vehicle-identification";
import { Kvps } from "@/lib/kvps";

interface VehicleIdentificationProps {
  onCanProceedChange?: (canProceed: boolean) => void;
  currentKvps?: Kvps | null;
}

type Vehicle = {
  vin: string;
  brand: string;
  model: string;
  color: string;
  engine: string;
  transmission: string;
  options: string;
  productionDate: string;
  status: string;
  isBodywork: boolean;
  hasTradeIn: boolean;
};

export default function VehicleIdentification({
  onCanProceedChange,
  currentKvps,
}: VehicleIdentificationProps) {
  const [chassisNumber, setChassisNumber] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [orderYear, setOrderYear] = useState("");

  const [loading, setLoading] = useState(false);
  const [vehicleFound, setVehicleFound] = useState(false);
  const [vehicleData, setVehicleData] = useState<Vehicle | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    onCanProceedChange?.(vehicleFound);
  }, [vehicleFound, onCanProceedChange]);

  const handleSearch = async () => {
    if (!chassisNumber && (!orderNumber || !orderYear)) {
      setError(
        "Veuillez saisir un numéro de châssis ou un numéro commande ainsi que l'année de production.",
      );
      return;
    }

    setLoading(true);
    setError("");
    setVehicleFound(false);
    setVehicleData(null);

    const searchData = chassisNumber
      ? { chassisNumber }
      : { orderNumber, orderYear };
    const searchVehicleResponse = await searchVehicle(searchData);

    if (!searchVehicleResponse.success) {
      setLoading(false);
      setError(searchVehicleResponse.message || "");
    } else {
      setLoading(false);
      setVehicleFound(true);
      setVehicleData(searchVehicleResponse.data as Vehicle);
      setError("");
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Search className="h-5 w-5 text-[#005EA8]" />
            <span>Identification du Véhicule</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              <p>
                Recherchez le véhicule par son VIN (17 caractères) ou son numéro
                et année de commande. La marque sera déterminée automatiquement
                selon le contexte KVPS{" "}
                <strong>{currentKvps?.identifier}</strong>.
              </p>
            </AlertDescription>
          </Alert>

          <FieldGroup className="grid grid-cols-8 gap-4">
            <Field className="col-span-3" data-invalid={false}>
              <FieldLabel htmlFor="chassisNumber">
                Numéro de Châssis (VIN)
              </FieldLabel>
              <Input
                id="chassisNumber"
                placeholder="WVWZZZ1KZPW123456"
                value={chassisNumber}
                onChange={(e) => setChassisNumber(e.target.value.toUpperCase())}
                maxLength={17}
                disabled={!!orderNumber || !!orderYear}
              />
              <FieldDescription>17 caractères alphanumériques</FieldDescription>
            </Field>
            <Field className="col-span-3" data-invalid={false}>
              <FieldLabel htmlFor="orderNumber">Numéro de Commande</FieldLabel>
              <Input
                id="orderNumber"
                placeholder="CMD-2024-001"
                value={orderNumber}
                maxLength={6}
                onChange={(e) => setOrderNumber(e.target.value)}
                disabled={!!chassisNumber}
              />
              <FieldDescription>Référence commande usine</FieldDescription>
            </Field>
            <Field className="col-span-2" data-invalid={false}>
              <FieldLabel htmlFor="orderDateYear">Année</FieldLabel>
              <Input
                id="orderDateYear"
                placeholder="2026"
                value={orderYear}
                maxLength={4}
                onChange={(e) => setOrderYear(e.target.value)}
                disabled={!!chassisNumber}
              />
              <FieldDescription>Année commande usine</FieldDescription>
            </Field>
          </FieldGroup>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Button
            onClick={() => handleSearch()}
            disabled={
              (!chassisNumber && (!orderNumber || !orderYear)) || loading
            }
            className="w-full"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Recherche en cours...
              </>
            ) : (
              <>
                <Search className="mr-2 h-4 w-4" />
                Rechercher Véhicule
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Skeleton Loader */}
      {loading && (
        <Card>
          <CardContent className="space-y-4 pt-6">
            <Skeleton className="h-6 w-1/3" />
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Vehicle Found */}
      {vehicleFound && vehicleData && !loading && (
        <Card className="border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-green-800">
              <CheckCircle className="h-5 w-5" />
              <span>Véhicule Trouvé</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <h4 className="mb-2 font-semibold text-gray-900">
                  Détails du Véhicule
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">VIN :</span>
                    <span className="font-medium">{vehicleData.vin}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Marque :</span>
                    <Badge className="bg-[#005EA8]">{vehicleData.brand}</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Modèle :</span>
                    <span className="font-medium">{vehicleData.model}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Couleur :</span>
                    <span className="font-medium">{vehicleData.color}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Moteur :</span>
                    <span className="font-medium">{vehicleData.engine}</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="mb-2 font-semibold text-gray-900">
                  Statut & Production
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Production :</span>
                    <span className="font-medium">
                      {vehicleData.productionDate}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Statut :</span>
                    <Badge className="bg-green-100 text-green-800">
                      {vehicleData.status}
                    </Badge>
                  </div>
                  {vehicleData.isBodywork && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Type :</span>
                      <Badge className="bg-orange-100 text-orange-800">
                        Véhicule à carrosser
                      </Badge>
                    </div>
                  )}
                  {vehicleData.hasTradeIn && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Reprise :</span>
                      <Badge className="bg-purple-100 text-purple-800">
                        Avec reprise
                      </Badge>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
